import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const NodeGraph = ({ data, searchTerm }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const popupRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [showNodeInfo, setShowNodeInfo] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Function to get node color based on type
  const getNodeColor = (node) => {
    if (node.type === 'primary') return '#6B5BA6'; // Purple color for primary nodes
    if (node.type === 'tertiary') return '#909090'; // Light gray for tertiary nodes
    return '#1D201F'; // Black color for secondary nodes
  };
  
  // Function to check if a string is a valid URL
  const isValidUrl = (string) => {
    if (!string) return false;
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };
  
  
  // Function to get embed URL for various platforms
  const getEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      
      // Handle YouTube URLs
      if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
        let videoId;
        
        if (urlObj.hostname.includes('youtube.com')) {
          // Regular youtube.com URL
          videoId = urlObj.searchParams.get('v');
        } else if (urlObj.hostname.includes('youtu.be')) {
          // Shortened youtu.be URL
          videoId = urlObj.pathname.substring(1);
        }
        
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?autoplay=0`;
        }
      }
      
      // Handle Vimeo URLs
      if (urlObj.hostname.includes('vimeo.com')) {
        const vimeoId = urlObj.pathname.substring(1);
        if (vimeoId) {
          return `https://player.vimeo.com/video/${vimeoId}`;
        }
      }
      
      // Return original URL for other sites
      return url;
    } catch (_) {
      return url;
    }
  };
  
  // Function to get a friendly name for a URL
  const getUrlFriendlyName = (url) => {
    try {
      const urlObj = new URL(url);
      
      // For YouTube, try to use video title (not possible client-side without API)
      if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
        return 'YouTube Video';
      }
      
      // For Vimeo
      if (urlObj.hostname.includes('vimeo.com')) {
        return 'Vimeo Video';
      }
      
      // For other sites, use the hostname without www.
      let hostname = urlObj.hostname;
      hostname = hostname.replace('www.', '');
      
      // Capitalize first letter of each word
      const parts = hostname.split('.');
      if (parts.length > 0) {
        return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      }
      
      return hostname;
    } catch (_) {
      return 'External Link';
    }
  };
  
  // Function to determine if URL should be embedded as iframe
  const shouldEmbed = (url) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      
      // List of domains that work well in iframes
      const embedDomains = [
        'youtube.com', 'youtu.be', 'vimeo.com', 'dailymotion.com',
        'codepen.io', 'jsfiddle.net', 'codesandbox.io', 'replit.com',
        'docs.google.com', 'slides.google.com', 'sheets.google.com',
        'figma.com', 'spotify.com', 'soundcloud.com',
        'maps.google.com', 'giphy.com', 'imgur.com'
      ];
      
      return embedDomains.some(domain => urlObj.hostname.includes(domain));
    } catch (_) {
      return false;
    }
  };

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (!data || !data.nodes || !data.links || !dimensions.width || !dimensions.height) {
      return;
    }

    const svg = d3.select(svgRef.current);
    
    // Clear existing elements
    svg.selectAll('*').remove();
    
    // Create a group for the graph
    const g = svg.append('g');

    // Create zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    // Apply zoom behavior
    svg.call(zoom);
    
    // Initial zoom to center
    const initialTransform = d3.zoomIdentity
      .translate(dimensions.width / 2, dimensions.height / 2);
    svg.call(zoom.transform, initialTransform);

    // Create force simulation
    const simulation = d3.forceSimulation(data.nodes)
      .force('link', d3.forceLink(data.links).id(d => d.id).distance(70))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(0, 0))
      .force('collision', d3.forceCollide().radius(15));

    // Create links
    const link = g.selectAll('.link')
      .data(data.links)
      .enter().append('line')
      .attr('class', 'link')
      .style('stroke', '#CCCCCC')
      .style('stroke-opacity', 0.5)
      .style('stroke-width', 1);

    // Create nodes
    const node = g.selectAll('.node')
      .data(data.nodes)
      .enter().append('g')
      .attr('class', 'node-group')
      .style('z-index', 10) // Ensure nodes have higher z-index
      .on('mouseenter', (event, d) => {
        // Find connected links and connected nodes
        const connectedLinks = data.links.filter(l => l.source.id === d.id || l.target.id === d.id);
        const connectedNodeIds = new Set([d.id]);
        
        // Add IDs of connected nodes to our set
        connectedLinks.forEach(link => {
          connectedNodeIds.add(link.source.id);
          connectedNodeIds.add(link.target.id);
        });
        
        // Highlight the hovered node
        d3.select(event.currentTarget).select('circle')
          .transition().duration(200)
          .attr('fill', '#E7C253'); // Gold hover color
        
        // Reduce opacity of non-connected nodes
        node.filter(n => !connectedNodeIds.has(n.id))
          .transition().duration(200)
          .style('opacity', 0.2);
        
        // Highlight connected links
        connectedLinks.forEach(connectedLink => {
          link.filter(l => l.source.id === connectedLink.source.id && l.target.id === connectedLink.target.id)
            .transition().duration(200)
            .style('stroke', '#E7C253') // Gold hover color
            .style('stroke-opacity', 1)
            .style('stroke-width', 2);
        });
        
        // Reduce opacity of non-connected links
        link.filter(l => {
          return !connectedLinks.some(cl => 
            cl.source.id === l.source.id && cl.target.id === l.target.id
          );
        })
        .transition().duration(200)
        .style('opacity', 0.2);
      })
      .on('mouseleave', (event, d) => {
        // Reset all node colors and opacities
        node.transition().duration(200)
          .style('opacity', 1);
          
        d3.select(event.currentTarget).select('circle')
          .transition().duration(200)
          .attr('fill', getNodeColor(d));
        
        // Reset all link colors and opacities
        link.transition().duration(200)
          .style('stroke', '#CCCCCC')
          .style('stroke-opacity', 0.5)
          .style('stroke-width', 1)
          .style('opacity', 1);
      })
      .on('click', (event, d) => {
        event.stopPropagation();
        
        // Get the position of the clicked node in screen coordinates
        const svgElement = svgRef.current;
        const svgRect = svgElement.getBoundingClientRect();
        const transform = d3.zoomTransform(svgElement);
        
        // Calculate the position in screen coordinates
        const x = transform.applyX(d.x) + svgRect.left;
        const y = transform.applyY(d.y) + svgRect.top;
        
        setNodePosition({ x, y });
        setSelectedNode(d);
        setShowNodeInfo(true);
      });

    // Add drag behavior
    const drag = d3.drag()
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended);

    node.call(drag);

    // Add circles to nodes
    node.append('circle')
      .attr('r', 6.4) // Reduced by 20% from original size of 8px
      .attr('fill', d => getNodeColor(d))
      .style('stroke', 'none')
      .style('pointer-events', 'all') // Ensure nodes receive mouse events

    // Add text labels under nodes
    node.append('text')
      .attr('dy', 18) // Position text 18px below node center
      .attr('text-anchor', 'middle') // Center text horizontally
      .attr('class', 'node-label')
      .style('font-size', '10px')
      .style('fill', '#333333')
      .style('pointer-events', 'none') // Don't capture mouse events on text
      .text(d => d.displayLabel || d.name || d.id) // Use displayLabel if available, fallback to name or id

    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node
        .attr('transform', d => `translate(${d.x},${d.y})`);

      // Update label positions if needed (though they'll follow their parent nodes)
      node.select('text')
        .attr('visibility', d => {
          // Hide labels if nodes are too close together based on zoom level
          const transform = d3.zoomTransform(svgRef.current);
          return transform.k < 0.5 ? 'hidden' : 'visible';
        });
    });

    // Add click handler on background to close selected node
    svg.on('click', () => {
      setShowNodeInfo(false);
      // Wait for animation to complete before clearing the selected node
      setTimeout(() => {
        setSelectedNode(null);
      }, 300);
    });

    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    return () => {
      simulation.stop();
    };
  }, [data, dimensions]);

  // Function to calculate the best position for node info popup to ensure it never goes off-page
  const calculateNodeInfoPosition = (position) => {
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Determine if we're on mobile
    const isMobile = window.innerWidth <= 768;
    
    // Default transform and origin
    let transform = 'translate(-50%, -50%)';
    let transformOrigin = 'center';
    
    // Different positioning strategies for mobile vs desktop
    if (isMobile) {
      // For mobile: simple fixed position that ensures visibility
      // Always center horizontally and position near the top with scrolling for content
      return {
        left: viewportWidth / 2, // Always center horizontally on mobile
        top: 50, // Fixed position from top to avoid shaking and ensure header visibility
        transformOrigin: 'center top',
        transform: 'translate(-50%, 0)', // Only center horizontally, anchor to top
      };
    } else {
      // Use a more conservative approach for desktop that guarantees visibility
      // For desktop devices, use a fixed position at the top center of the screen
      // This is the safest approach to ensure nothing gets cut off
      return {
        left: viewportWidth / 2, // Center horizontally
        top: 80, // Fixed position from top for desktop (slightly lower than mobile)
        transformOrigin: 'center top',
        transform: 'translate(-50%, 0)', // Only center horizontally, anchor to top
      };
    }
  };
  
  // Add resize listener to reposition popup when window size changes
  useEffect(() => {
    if (!selectedNode || !showNodeInfo) return;
    
    const handleResize = () => {
      // Only recalculate on actual window size changes
      const newViewportWidth = window.innerWidth;
      const newViewportHeight = window.innerHeight;
      
      // Store viewport dimensions to avoid excessive recalculations
      setNodePosition(prev => ({
        ...prev,
        viewportWidth: newViewportWidth,
        viewportHeight: newViewportHeight
      }));
    };
    
    // Fix for mobile - use a single adjustments approach
    const fixPopupPosition = () => {
      // Don't reposition in fullscreen mode
      if (isFullScreen || !popupRef.current) return;
      
      const isMobile = window.innerWidth <= 768;
      
      // On mobile, use a simpler positioning strategy to avoid shaking
      if (isMobile) {
        // Always center horizontally with fixed top position on mobile
        const viewportWidth = window.innerWidth;
        
        // Apply the same fixed positioning as in calculateNodeInfoPosition
        const newPos = {
          x: viewportWidth / 2,
          y: 50  // Fixed position from top to prevent shaking
        };
        
        // Only update if significantly different to avoid loops
        if (Math.abs(newPos.x - nodePosition.x) > 5 || Math.abs(newPos.y - nodePosition.y) > 5) {
          setNodePosition(newPos);
        }
      } else {
        // Desktop positioning - use bounding rect check
        const rect = popupRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Only make significant adjustments to avoid tiny adjustments causing loops
        let needsRepositioning = false;
        let newPos = { ...nodePosition };
        
        // Check horizontal boundaries with minimum adjustment threshold
        if (rect.left < 10) {
          newPos.x += Math.max(15, (10 - rect.left));
          needsRepositioning = true;
        } else if (rect.right > viewportWidth - 10) {
          newPos.x -= Math.max(15, (rect.right - (viewportWidth - 10)));
          needsRepositioning = true;
        }
        
        // Check vertical boundaries with minimum adjustment threshold
        if (rect.top < 10) {
          newPos.y += Math.max(15, (10 - rect.top));
          needsRepositioning = true;
        } else if (rect.bottom > viewportHeight - 10) {
          newPos.y -= Math.max(15, (rect.bottom - (viewportHeight - 10)));
          needsRepositioning = true;
        }
        
        // Only update for significant changes
        if (needsRepositioning) {
          setNodePosition(newPos);
        }
      }
    };
    
    // Run the fix once after render
    const initialFixTimeout = setTimeout(fixPopupPosition, 50);
    
    // Handle window resize
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(initialFixTimeout);
    };
  }, [selectedNode, showNodeInfo, isFullScreen]); // Remove nodePosition from dependencies to prevent loops
  
  // Calculate position when popup is shown
  const nodeInfoPosition = isFullScreen ? null : calculateNodeInfoPosition(nodePosition);
  
  return (
    <div className="graph-container" ref={containerRef}>
      <svg ref={svgRef} className="graph" width="100%" height="100%" style={{ background: '#FAF6EF', position: 'relative', zIndex: 2 }} />
      {selectedNode && (
        <div 
          ref={popupRef}
          className={`node-info ${showNodeInfo ? 'show' : 'hide'} ${isFullScreen ? 'fullscreen' : ''}`}
          style={{
            position: 'absolute',
            left: isFullScreen ? '50%' : `${nodeInfoPosition?.left}px`,
            top: isFullScreen ? '50%' : `${nodeInfoPosition?.top}px`,
            transform: isFullScreen ? 'translate(-50%, -50%)' : nodeInfoPosition?.transform,
            padding: window.innerWidth <= 768 ? '15px' : '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 100,
            maxWidth: isFullScreen ? '90%' : (window.innerWidth <= 768 ? 'calc(100vw - 30px)' : '600px'),
            width: isFullScreen ? '90%' : (window.innerWidth <= 768 ? 'calc(100vw - 30px)' : '100%'),
            maxHeight: isFullScreen ? '90vh' : (window.innerWidth <= 768 ? '80vh' : '80vh'),
            height: isFullScreen ? '90vh' : 'auto',
            transformOrigin: nodeInfoPosition?.transformOrigin || 'center center',
            overflow: 'auto', // Allow scrolling for content in all formats
            overflowY: 'auto', // Explicit vertical scrolling
            display: 'flex',
            flexDirection: 'column',
            fontSize: window.innerWidth <= 768 ? '0.95rem' : '1rem',
          }}
        >
          <div className="node-info-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', position: 'relative', flexShrink: 0 }}>
            <h3 style={{ margin: '0', color: '#333', paddingRight: '60px' }}>{selectedNode.name}</h3>
            <div style={{ display: 'flex', position: 'absolute', right: '0', top: '0' }}>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFullScreen(!isFullScreen);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#999',
                  marginRight: '2px'
                }}
                title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
              >
                {isFullScreen ? '⊖' : '⊕'}
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNodeInfo(false);
                  setIsFullScreen(false);
                  setTimeout(() => setSelectedNode(null), 300);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#999'
                }}
                title="Close"
              >
                ×
              </button>
            </div>
          </div>
          
          <div className="node-info-content" style={{ 
              overflowY: 'auto', 
              flexGrow: 1, 
              paddingRight: '5px',
              maxHeight: window.innerWidth <= 768 ? 'calc(80vh - 60px)' : 'calc(85vh - 100px)', // Ensure content stays within viewport
            }}>
            {selectedNode.content && isValidUrl(selectedNode.content) ? (
              <div>
                <div className="url-preview">
                  {shouldEmbed(selectedNode.content) ? (
                    <div style={{
                      position: 'relative',
                      paddingBottom: '56.25%', /* 16:9 aspect ratio */
                      height: 0,
                      overflow: 'hidden',
                      maxWidth: '100%',
                      marginTop: '15px',
                      marginBottom: '15px',
                      ...(isFullScreen ? {
                        maxWidth: '90%',
                        margin: '20px auto'
                      } : {})
                    }}>
                      <iframe 
                        src={getEmbedUrl(selectedNode.content)} 
                        title="URL Preview"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: '1px solid #eee',
                          borderRadius: '4px',
                        }}
                        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                        allowFullScreen
                        frameBorder="0"
                      />
                    </div>
                  ) : (
                    <div className="external-link-message" style={{ padding: '20px', textAlign: 'center', background: '#f5f5f5', borderRadius: '4px', marginTop: '10px' }}>
                      <p style={{ margin: '0 0 10px 0' }}>{getUrlFriendlyName(selectedNode.content)}</p>
                      <a 
                        href={selectedNode.content} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'inline-block', 
                          padding: '8px 16px', 
                          background: '#6B5BA6', 
                          color: 'white', 
                          borderRadius: '4px', 
                          textDecoration: 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div 
                className="text-content" 
                style={{ margin: 0, color: '#555', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: selectedNode.content || 'No content' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeGraph;
